import config from '@packages/config';
import { filterTimeControl } from '@packages/shared/src/utils/filterTimeControl/filterTimeControl';
import { getLocalTime } from '@packages/shared/src/utils/filterTimeControl/toLocalTime';
import type {
  Audience,
  ParagraphType,
  PromotionItemEntityReference,
  TimeControl,
} from '../../interfaces';
import type { ContentSnippetType } from '../../interfaces/contentSnippet';
import type { TaxonomyTerm } from '../../interfaces/taxonomy';

const createFilterForCurrentAudiences =
  (audiences: Array<Audience[keyof Audience]>) =>
  (item: ParagraphType | ContentSnippetType | PromotionItemEntityReference): boolean => {
    if ('taxonomy_term' in item && Array.isArray(item.taxonomy_term)) {
      const taxonomyTerms = item.taxonomy_term;
      // if we find a taxonomy_term with the name that is in audiences, we return true
      return taxonomyTerms.every(
        (t: TaxonomyTerm) =>
          t.field_name !== 'field_audience' ||
          (t.field_name === 'field_audience' && audiences.includes(t.name)),
      );
    }
    if ('field_user_status' in item && item.field_user_status) {
      // if there is no user status set for this banner -> any audience fits
      return (
        ('data' in item.field_user_status && item.field_user_status.data === null) ||
        // if there is a user status set for this banner -> check it against the current user audience hierarchy
        (item.field_user_status.name && audiences.includes(item.field_user_status.name))
      );
    }
    // if there is no taxonomy_term at all, we always want to display it and return true
    return true;
  };

const createFilterForTimeControl = (testDate: string | string[] | undefined | null = undefined) => {
  const { officeTimes, timeZone } = config;

  return (item: TimeControl): boolean => {
    if (
      'field_valid_from' in item ||
      'field_valid_to' in item ||
      'field_visible_from' in item ||
      'field_visible_until' in item ||
      'behavior_paragraph_time_control_valid_from' in item ||
      'behavior_paragraph_time_control_valid_until' in item ||
      'behavior_paragraph_visibility_officehours' in item ||
      'field_promotion_valid_from_date' in item ||
      'field_promotion_valid_until_date' in item
    ) {
      // Lazy evaluation: only call getLocalTime when actually filtering an item
      return filterTimeControl(
        item,
        testDate || getLocalTime(timeZone).toISOString(),
        officeTimes,
        timeZone,
      );
    }
    // if there is no date fields at all, we always want to display it and return true
    return true;
  };
};

export { createFilterForCurrentAudiences, createFilterForTimeControl };
